import React, { useState, useRef, useEffect } from "react";
import {
  FaCog,
  FaBell,
  FaBars,
  FaTimes,
  FaUser,
  FaSignOutAlt,
  FaEnvelope,
} from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Apiurl from "../environment/Apiurl";
import "../navbar/Navbar.css";

const Navbar = ({ collapsed, toggleMobileSidebar, isMobile, sidebarOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserActions, setShowUserActions] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);

  const notificationRef = useRef(null);
  const userActionsRef = useRef(null);

  // User info
  const [username, setUsername] = useState("");
  const [userType, setUserType] = useState("");
  const [name, setName] = useState("");
  const [profileIcon, setProfileIcon] = useState("");

  useEffect(() => {
    setUsername(localStorage.getItem("username") || "User");
    setUserType(localStorage.getItem("user_id") || "Role");
    setName(localStorage.getItem("role") || "Admin");

    const storedIcon = localStorage.getItem("profile_icon");
    if (storedIcon) {
      setProfileIcon(storedIcon);
    }
  }, []);

  // Breadcrumb Map
  const breadcrumbMap = {
    "/": { title: "Login", parent: null },
    "/dashboard": { title: "Dashboard", parent: null },
    "/bill": { title: "Bill", parent: "/dashboard" },
    "/masters": { title: "Masters", parent: "/dashboard" },
    "/users": { title: "Users", parent: "/dashboard" },
    "/roles": { title: "Roles", parent: "/dashboard" },
    "/reports": { title: "Reports", parent: "/dashboard" },
    "/notifications": { title: "Notifications", parent: "/dashboard" },
    "/bill/general": { title: "General", parent: "/bill" },
    "/bill/gst-in": { title: "GST-IN", parent: "/bill" },
    "/masters/brand": { title: "Brand", parent: "/masters" },
    "/masters/models": { title: "Models", parent: "/masters" },
    "/masters/charges": { title: "Charges", parent: "/masters" },
    "/masters/staffs": { title: "Staffs", parent: "/masters" },
    "/roles/admin": { title: "Admin", parent: "/roles" },
    "/roles/manager": { title: "Manager", parent: "/roles" },
    "/roles/receptionist": { title: "Receptionist", parent: "/roles" },
    "/roles/add": { title: "Add Role", parent: "/roles" },
    "/roles/edit/:id": { title: "Edit Role", parent: "/roles" },
  };

  // Capitalize helper
  const capitalizeFirstLetter = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Generate Breadcrumbs
  const generateBreadcrumbs = () => {
    const breadcrumbs = [];
    let currentPath = location.pathname;

    if (breadcrumbMap[currentPath]) {
      let path = currentPath;
      while (path && breadcrumbMap[path]) {
        breadcrumbs.unshift({
          title: capitalizeFirstLetter(breadcrumbMap[path].title),
          path: path,
        });
        path = breadcrumbMap[path].parent;
      }
      return breadcrumbs;
    }

    const pathParts = currentPath.split("/");
    for (let i = pathParts.length; i > 0; i--) {
      const testPath = pathParts.slice(0, i).join("/");
      const remaining = pathParts.slice(i).join("/");

      if (breadcrumbMap[testPath]) {
        let path = testPath;
        while (path && breadcrumbMap[path]) {
          breadcrumbs.unshift({
            title: capitalizeFirstLetter(breadcrumbMap[path].title),
            path: path,
          });
          path = breadcrumbMap[path].parent;
        }

        if (remaining) {
          breadcrumbs.push({
            title: capitalizeFirstLetter(
              remaining.split("/")[0].replace("-", " "),
            ),
            path: currentPath,
          });
        }

        return breadcrumbs;
      }
    }

    return [
      {
        title: capitalizeFirstLetter(
          currentPath.split("/").pop() || "Dashboard",
        ),
        path: currentPath,
      },
    ];
  };

  const breadcrumbs = generateBreadcrumbs();

  // Notifications
  const fetchNotifications = async () => {
    setIsLoadingNotifications(true);
    try {
      const response = await axios.get(`${Apiurl}/notification`, {
        params: { status: "pending" },
      });

      const notificationsData = response.data || [];
      setNotifications(notificationsData);

      const unreadNotifications = notificationsData.filter(
        (n) => !n.staff_viewed && n.status === "pending",
      ).length;

      setUnreadCount(unreadNotifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  useEffect(() => {
    const roleNames = localStorage.getItem("role_name");
    if (roleNames === "Management") {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, []);

  // Logout
  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  // Notification utils
  const markAsRead = async (id) => {
    try {
      await axios.put(`${Apiurl}/notification/${id}`, { staff_viewed: true });
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, staff_viewed: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification:", error);
    }
  };

  const formatNotificationTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
    if (diffInHours < 24) return `${diffInHours} hr ago`;
    if (diffInDays === 1) return "Yesterday";
    if (diffInDays < 7) return `${diffInDays} days ago`;
    return date.toLocaleDateString();
  };

  const getNotificationTitle = (n) => {
    const actionText =
      n.action_type.charAt(0).toUpperCase() + n.action_type.slice(1);
    const tableText = n.table_name
      .replace("_", " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
    return `${actionText} ${tableText}`;
  };

  const getNotificationMessage = (n) =>
    `${n.employee_name} requested to ${n.action_type} ${n.table_name} (ID: ${n.table_id})`;

  const isManagement = localStorage.getItem("role_name") === "Management";

  return (
    <nav
      className={`navbar sticky-top bg-white border-bottom shadow-sm overflow-visible ${
        collapsed ? "sidebar-collapsed" : ""
      }`}
    >
      <div className="container-fluid d-flex justify-content-between align-items-center">
        {/* Left Section */}
        <div className="d-flex align-items-center">
          {isMobile && (
            <button
              className="btn btn-link p-0 me-2"
              onClick={toggleMobileSidebar}
            >
              {sidebarOpen ? <FaTimes /> : <FaBars />}
            </button>
          )}

          {/* Breadcrumbs */}
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              {breadcrumbs.map((breadcrumb, index) => (
                <li
                  key={index}
                  className={`breadcrumb-item ${
                    index === breadcrumbs.length - 1 ? "breadcrumb-current" : ""
                  }`}
                >
                  {index < breadcrumbs.length - 1 ? (
                    ["Bill", "Masters", "Roles"].includes(breadcrumb.title) ? (
                      <span className="fw-bold text-dark">
                        {breadcrumb.title}
                      </span>
                    ) : (
                      <Link
                        to={breadcrumb.path}
                        className="text-decoration-none breadcrumb-link"
                      >
                        {breadcrumb.title}
                      </Link>
                    )
                  ) : (
                    breadcrumb.title
                  )}
                </li>
              ))}
              {location.pathname === "/dashboard" && (
                <li className="breadcrumb-item">
                  <Link
                    to="/bill/general/add-general-bill"
                    className="btn btn-sm ms-2"
                    style={{ backgroundColor: "#2e9290", color: "white" }}
                  >
                    Add General Bill
                  </Link>
                </li>
              )}
            </ol>
          </nav>
        </div>

        {/* Right Section */}
        <div className="navbar-icons">
          {isManagement && (
            <div className="notification-container" ref={notificationRef}>
              <div
                className="navbar-icon-wrapper"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <FaBell className="navbar-icon" />
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount}</span>
                )}
              </div>
              {showNotifications && (
                <div className="notifications-dropdown">
                  <div className="notifications-header">
                    <h4>Notifications</h4>
                    {unreadCount > 0 && (
                      <span className="unread-count">{unreadCount} Unread</span>
                    )}
                  </div>
                  <div className="notifications-list">
                    {isLoadingNotifications ? (
                      <div className="loading">Loading...</div>
                    ) : notifications.length === 0 ? (
                      <div className="no-notifications">No notifications</div>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n._id}
                          className={`notification-item ${
                            n.staff_viewed ? "read" : "unread"
                          }`}
                          onClick={() => markAsRead(n._id)}
                        >
                          <div className="notification-content">
                            <h5>{getNotificationTitle(n)}</h5>
                            <p>{getNotificationMessage(n)}</p>
                            <span className="notification-time">
                              {formatNotificationTime(n.created_at)}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* User Actions */}
          <div className="user-actions-container" ref={userActionsRef}>
            <div
              className="navbar-user"
              onClick={() => setShowUserActions(!showUserActions)}
            >
              {profileIcon ? (
                <img src={profileIcon} alt="User" className="user-avatar-img" />
              ) : (
                <div className="user-avatar-placeholder">
                  {name?.charAt(0).toUpperCase() || "U"}
                </div>
              )}
              <span className="navbar-user-dropdown">▼</span>
            </div>
            {showUserActions && (
              <div className="user-actions-dropdown">
                <div className="user-info">
                  {profileIcon ? (
                    <img
                      src={profileIcon}
                      alt="User"
                      className="user-avatar-img"
                    />
                  ) : (
                    <div className="user-avatar-placeholder">
                      {name?.charAt(0).toUpperCase() || "U"}
                    </div>
                  )}
                  <div className="user-details">
                    <h4>{name}</h4>
                    <p>
                      {username} / {userType}
                    </p>
                  </div>
                </div>

                <button
                  className="user-action-item logout-btn"
                  onClick={handleLogout}
                >
                  <FaSignOutAlt className="action-icon" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
